import { useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    Button,
    Stepper,
    Step,
    StepLabel,
    Alert,
    CircularProgress,
    Stack,
    Container,
    Tooltip,
} from '@mui/material';
import {
    Error as ErrorIcon,
    Settings as SettingsIcon,
    Storage as StorageIcon,
    Email as EmailIcon,
    Event as EventIcon,
    Security as SecurityIcon,
    Schedule as ScheduleIcon,
    Cloud as CloudIcon,
} from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { setActiveStep, setSubmitting } from '@/store/installSlice';
import { useGetInstallStatusQuery, usePostInstallStepMutation } from '@/store/apiSlice';
import type { InstallConfigRequest } from '@/types';
import { BasicConfigStep } from '@/pages/install/BasicConfigStep';
import { AuthenticationStep } from '@/pages/install/AuthenticationStep';
import { MailStep } from '@/pages/install/MailStep';
import { CalendarStep } from '@/pages/install/CalendarStep';
import { ChurchToolsStep } from '@/pages/install/ChurchToolsStep';
import { ScheduleStep } from '@/pages/install/ScheduleStep';
import { DatabaseStep } from '@/pages/install/DatabaseStep';

const InstallPage = () => {
    const { LL, setLocale } = useI18nContext();
    const dispatch = useAppDispatch();

    const { formData, activeStep, visitedSteps, stepErrors, submitting } = useAppSelector(
        (state) => state.install
    );

    // Sync locale with formData.defaultLanguage on mount and when it changes
    useEffect(() => {
        if (formData.defaultLanguage) {
            setLocale(formData.defaultLanguage as 'en' | 'de');
        }
    }, [formData.defaultLanguage, setLocale]);

    const steps = [
        LL.install.steps.basicConfig(),
        LL.install.steps.authSettings(),
        LL.install.steps.emailSettings(),
        LL.install.steps.calendarSettings(),
        LL.install.steps.churchToolsIntegration(),
        LL.install.steps.scheduleFeature(),
        LL.install.steps.databaseInstallation(),
    ];

    // Use RTK Query hook for install status
    const {
        data: installStatus,
        isLoading: statusLoading,
        isError: statusError,
        refetch: refetchInstallStatus,
    } = useGetInstallStatusQuery();

    // React to changes from the RTK Query hook
    useEffect(() => {
        if (statusLoading || statusError) return;

        if (!installStatus) return;

        if (installStatus.installed) {
            window.location.href = '/';
            return;
        }

        if (installStatus.reason === 'database' || installStatus.step === 'database') {
            dispatch(setActiveStep(6));
            return;
        }
    }, [installStatus, statusLoading, statusError, dispatch]);

    const handleNext = () => {
        dispatch(setActiveStep(activeStep + 1));
    };

    const handleBack = () => {
        dispatch(setActiveStep(activeStep - 1));
    };

    const [postInstallStep] = usePostInstallStepMutation();

    const handleConfigSubmit = async () => {
        // Check if any step has errors
        const hasErrors = Object.values(stepErrors).some((errors) => errors.length > 0);
        if (hasErrors) {
            return; // Don't submit if there are errors
        }

        dispatch(setSubmitting(true));

        try {
            const {
                scheduleMinDuration,
                scheduleDefaultSetupTime,
                scheduleDefaultTeardownTime,
                ...other
            } = formData;

            const payload: InstallConfigRequest = {
                step: 'config',
                scheduleMinDuration: Number(scheduleMinDuration),
                scheduleDefaultSetupTime: Number(scheduleDefaultSetupTime),
                scheduleDefaultTeardownTime: Number(scheduleDefaultTeardownTime),
                ...other,
            } as InstallConfigRequest;

            const data = await postInstallStep(payload).unwrap();

            if (data.success) {
                // Move user to database step and refresh install status from the server
                setTimeout(() => {
                    dispatch(setActiveStep(6));
                    refetchInstallStatus();
                }, 1500);
            }
        } catch (err: any) {
            console.error('Config failed:', err);
        } finally {
            dispatch(setSubmitting(false));
        }
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return <BasicConfigStep />;
            case 1:
                return <AuthenticationStep />;
            case 2:
                return <MailStep />;
            case 3:
                return <CalendarStep />;
            case 4:
                return <ChurchToolsStep />;
            case 5:
                return <ScheduleStep />;
            case 6:
                return <DatabaseStep />;
            default:
                return <Typography>Step {activeStep} - Unknown</Typography>;
        }
    };

    const getStepIcon = (step: number) => {
        const icons = [
            <SettingsIcon />,
            <SecurityIcon />,
            <EmailIcon />,
            <EventIcon />,
            <CloudIcon />,
            <ScheduleIcon />,
            <StorageIcon />,
        ];
        return icons[step];
    };

    const currentStepErrors = stepErrors[activeStep] || [];
    const hasErrors = Object.values(stepErrors).some((errors) => errors.length > 0);

    if (statusLoading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
            >
                <CircularProgress sx={{ color: 'white' }} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                py: 4,
                px: 2,
            }}
        >
            <Container maxWidth="md">
                <Card sx={{ boxShadow: '0 10px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                    {/* Header */}
                    <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        <Typography variant="h5" component="h1" gutterBottom>
                            {LL.install.title()}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {LL.install.subtitle()}
                        </Typography>
                    </Box>

                    {/* Step Errors */}
                    {currentStepErrors.length > 0 && (
                        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                            <Alert severity="error" icon={<ErrorIcon />}>
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 600,
                                    }}
                                >
                                    {LL.install.messages.pleaseFixErrors()}
                                </Typography>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {currentStepErrors.map((error, idx) => (
                                        <li key={idx}>{error}</li>
                                    ))}
                                </ul>
                            </Alert>
                        </Box>
                    )}

                    <Stack direction="row" sx={{ minHeight: 500 }}>
                        {/* Stepper */}
                        <Stack
                            sx={{
                                borderRight: 1,
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                px: 4,
                                py: 2,
                                display: { xs: 'none', md: 'block' },
                            }}
                        >
                            <Stepper
                                activeStep={activeStep}
                                orientation="vertical"
                                sx={{
                                    userSelect: 'none',
                                    '& .MuiStepLabel-root .Mui-completed': {
                                        color: 'success.main',
                                    },
                                    '& .MuiStepLabel-root .Mui-active': {
                                        color: 'primary.main',
                                    },
                                    '& .MuiStepLabel-label.Mui-active': {
                                        fontWeight: 600,
                                    },
                                }}
                            >
                                {steps.map((label, index) => {
                                    const stepHasErrors = (stepErrors[index] || []).length > 0;
                                    const stepIsVisited = visitedSteps.includes(index);
                                    const stepIsCompleted =
                                        stepIsVisited && !stepHasErrors && index < activeStep;
                                    const canNavigate = index < activeStep || stepIsVisited;

                                    return (
                                        <Step key={label} completed={stepIsCompleted}>
                                            <StepLabel
                                                icon={getStepIcon(index)}
                                                error={stepHasErrors && stepIsVisited}
                                                onClick={() => {
                                                    if (canNavigate && index !== activeStep) {
                                                        dispatch(setActiveStep(index));
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiStepLabel-iconContainer': {
                                                        pr: 1,
                                                    },
                                                    cursor: canNavigate ? 'pointer' : 'default',
                                                    '&:hover': canNavigate
                                                        ? {
                                                              '& .MuiStepLabel-label': {
                                                                  color: 'primary.main',
                                                              },
                                                          }
                                                        : {},
                                                }}
                                            >
                                                <Typography variant="body2">{label}</Typography>
                                            </StepLabel>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                        </Stack>

                        {/* Content */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box
                                    sx={{
                                        flex: 1,
                                        mb: 3,
                                        maxWidth: 600,
                                        mx: 'auto',
                                        width: '100%',
                                    }}
                                >
                                    {renderStepContent()}
                                </Box>

                                {/* Navigation */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        pt: 2,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        maxWidth: 600,
                                        mx: 'auto',
                                        width: '100%',
                                    }}
                                >
                                    {activeStep > 0 && activeStep < 6 && (
                                        <Button onClick={handleBack} disabled={submitting}>
                                            {LL.common.back()}
                                        </Button>
                                    )}

                                    <Box sx={{ flex: 1 }} />

                                    {activeStep < 5 && (
                                        <Button
                                            variant="contained"
                                            onClick={handleNext}
                                            disabled={submitting}
                                        >
                                            {LL.install.buttons.next()}
                                        </Button>
                                    )}

                                    {activeStep === 5 && (
                                        <Tooltip
                                            title={
                                                hasErrors ? LL.install.messages.configTooltip() : ''
                                            }
                                            arrow
                                        >
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleConfigSubmit}
                                                    disabled={submitting || hasErrors}
                                                    startIcon={
                                                        submitting ? (
                                                            <CircularProgress size={20} />
                                                        ) : (
                                                            <SettingsIcon />
                                                        )
                                                    }
                                                >
                                                    {submitting
                                                        ? LL.install.buttons.creatingConfiguration()
                                                        : LL.install.buttons.createConfiguration()}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Stack>
                </Card>
            </Container>
        </Box>
    );
};

export default InstallPage;
